import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getFavorites, toggleFavorite, isFavorite } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowSquareOut, CurrencyBtc, TrendUp, Star, ArrowsClockwise, LinkSimple
} from "@phosphor-icons/react";
import MarketSentiment from "@/components/MarketSentiment";
import TrendingAlerts from "@/components/TrendingAlerts";

function formatPrice(price, symbol) {
  if (!price && price !== 0) return "$0.00";
  if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.001) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
}

function formatVolume(vol) {
  if (!vol) return "";
  if (vol >= 1e9) return `$${(vol / 1e9).toFixed(2)}B`;
  if (vol >= 1e6) return `$${(vol / 1e6).toFixed(2)}M`;
  if (vol >= 1e3) return `$${(vol / 1e3).toFixed(1)}K`;
  return `$${vol.toFixed(0)}`;
}

function AssetCard({ asset, onFavToggle }) {
  const navigate = useNavigate();
  const { user, updateWatchlist } = useAuth();
  const isPositive = asset.change_24h > 0;
  const isFlat = !asset.change_24h;
  const isCrypto = asset.type === "crypto";
  const isInWatchlist = user?.watchlist?.includes(asset.id);
  const [faved, setFaved] = useState(isInWatchlist || isFavorite(asset.id, asset.type));

  useEffect(() => {
    if (user?.watchlist) setFaved(user.watchlist.includes(asset.id));
  }, [user, asset.id]);

  const handleClick = () => {
    try {
      const recents = JSON.parse(localStorage.getItem("market_recents") || "[]")
        .filter(r => !(r.id === asset.id && r.type === asset.type));
      recents.unshift({ id: asset.id, symbol: asset.symbol, name: asset.name, type: asset.type });
      localStorage.setItem("market_recents", JSON.stringify(recents.slice(0, 12)));
    } catch {}
    navigate(`/asset/${asset.type}/${encodeURIComponent(asset.id)}`);
  };

  const handleStar = async (e) => {
    e.stopPropagation();
    const newFaved = !faved;
    setFaved(newFaved);
    toggleFavorite({ id: asset.id, symbol: asset.symbol, name: asset.name, type: asset.type });
    if (user) {
      try {
        await updateWatchlist(asset.id, newFaved ? "add" : "remove");
      } catch {}
    }
    if (onFavToggle) onFavToggle();
  };

  // Color tone based on % change — applied to the soft tint behind the price
  // and the left accent strip.
  const tone = isFlat ? "amber" : (isPositive ? "emerald" : "red");
  const toneClass = `grad-${tone}`;
  const accent = isFlat
    ? "bg-zinc-700"
    : isPositive
      ? "bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-600"
      : "bg-gradient-to-b from-red-400 via-red-500 to-red-600";

  return (
    <div
      data-testid={`asset-card-${asset.symbol?.toLowerCase()}`}
      className={`relative overflow-hidden w-full text-left bg-zinc-900/40 border border-zinc-800/80 p-3.5 rounded-sm group glow-hover ${toneClass}`}
    >
      {/* Left accent strip — colored by performance */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${accent}`} aria-hidden="true" />

      <button
        data-testid={`fav-toggle-${asset.symbol?.toLowerCase()}`}
        onClick={handleStar}
        className="absolute top-2.5 right-2.5 z-10 p-1 rounded-sm hover:bg-zinc-800 transition-colors"
        title={faved ? "Remove from favorites" : "Add to favorites"}
      >
        <Star
          size={14}
          weight={faved ? "fill" : "regular"}
          className={`transition-colors ${faved ? "text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.6)]" : "text-zinc-600 group-hover:text-zinc-400"}`}
        />
      </button>
      <button onClick={handleClick} className="w-full text-left">
        <div className="flex items-center justify-between mb-2 pr-5">
          <div className="flex items-center gap-2">
            {isCrypto ? <CurrencyBtc size={14} className="text-zinc-500" /> : <TrendUp size={14} className="text-zinc-500" />}
            <span className="text-[11px] font-medium text-zinc-400 tracking-wide">{asset.symbol}</span>
          </div>
          <Badge className={`text-[10px] px-1.5 py-0 border-0 rounded-sm font-data flex items-center gap-0.5 ${
            isFlat ? "bg-zinc-800 text-zinc-400"
            : isPositive ? "bg-emerald-500/15 text-emerald-300 shadow-[0_0_8px_-4px_rgba(52,211,153,0.6)]"
            : "bg-red-500/15 text-red-300 shadow-[0_0_8px_-4px_rgba(239,68,68,0.6)]"
          }`}>
            {!isFlat && (isPositive ? "▲" : "▼")}
            {isPositive ? "+" : ""}{asset.change_24h?.toFixed(2)}%
          </Badge>
        </div>
        <div className={`text-lg font-data font-medium leading-none ${
          isFlat ? "text-zinc-50" : isPositive ? "text-emerald-100" : "text-red-100"
        }`}>
          {formatPrice(asset.current_price, asset.symbol)}
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-zinc-500 truncate">{asset.name}</span>
          {asset.volume_24h > 0 && (
            <span className="text-[10px] text-zinc-600">Vol {formatVolume(asset.volume_24h)}</span>
          )}
        </div>
        {isCrypto && asset.contract_address && (
          <div className="flex items-center gap-1.5 mt-1.5" data-testid={`contract-${asset.symbol?.toLowerCase()}`}>
            <LinkSimple size={9} className="text-zinc-600 flex-shrink-0" />
            <span className="text-[9px] text-zinc-600 font-data">
              {asset.contract_address.slice(0, 6)}...{asset.contract_address.slice(-4)}
            </span>
            {asset.explorer_url && (
              <a
                href={asset.explorer_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[8px] text-zinc-500 hover:text-blue-400 transition-colors flex items-center gap-0.5"
                data-testid={`explorer-${asset.symbol?.toLowerCase()}`}
              >
                <ArrowSquareOut size={8} />
                Scan
              </a>
            )}
          </div>
        )}
        {isCrypto && !asset.contract_address && asset.explorer_url && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <a
              href={asset.explorer_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[8px] text-zinc-500 hover:text-blue-400 transition-colors flex items-center gap-0.5"
              data-testid={`explorer-${asset.symbol?.toLowerCase()}`}
            >
              <ArrowSquareOut size={8} />
              Blockchain
            </a>
          </div>
        )}
      </button>
    </div>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favs, setFavs] = useState(getFavorites());
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);

  const refreshFavs = useCallback(() => setFavs(getFavorites()), []);

  const fetchFeatured = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/featured`);
      setAssets(resp.data);
      setLastRefresh(Date.now());
    } catch (e) {
      console.error("Failed to fetch featured:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatured();
    const onFavChange = () => refreshFavs();
    window.addEventListener("favorites-changed", onFavChange);

    // Auto-refresh prices every 30 seconds
    const interval = setInterval(() => fetchFeatured(true), 30000);
    return () => {
      window.removeEventListener("favorites-changed", onFavChange);
      clearInterval(interval);
    };
  }, [refreshFavs, fetchFeatured]);

  // Combine local (localStorage) favs + backend watchlist (signed-in users)
  const watchlistIds = user?.watchlist || [];
  const combinedFavMap = new Map();
  favs.forEach(f => combinedFavMap.set(`${f.type}:${f.id}`, f));
  watchlistIds.forEach(id => {
    const match = assets.find(a => a.id === id);
    if (match) combinedFavMap.set(`${match.type}:${match.id}`, {
      id: match.id, symbol: match.symbol, name: match.name, type: match.type,
    });
  });
  const combinedFavs = Array.from(combinedFavMap.values());

  // Merge favorites with price data from fetched assets
  const favAssets = combinedFavs.map(f => {
    const match = assets.find(a => a.id === f.id && a.type === f.type);
    return match || { ...f, current_price: 0, change_24h: 0, volume_24h: 0 };
  });

  // Cryptocurrency section: BTC, ETH, SOL, BNB only
  const CORE_CRYPTO_IDS = ["bitcoin", "ethereum", "solana", "binancecoin"];
  const cryptoAssets = CORE_CRYPTO_IDS
    .map(id => assets.find(a => a.id === id && a.type === "crypto"))
    .filter(Boolean);
  const otherAssets = assets.filter(a => a.type !== "crypto");

  // Market pulse — count of gainers vs losers, used in the hero strip
  const gainers = assets.filter(a => a.change_24h > 0).length;
  const losers = assets.filter(a => a.change_24h < 0).length;
  const totalAssets = assets.length || 1;
  const greenPct = Math.round((gainers / totalAssets) * 100);

  return (
    <div className="relative" data-testid="dashboard">
      {/* THE NEST — full-viewport bubble navigator over the artwork wallpaper.
          Uses 100svh so it fits cleanly on iPad / mobile without overflow. */}
      <section
        className="relative w-full flex items-center justify-center"
        style={{ height: "calc(100svh - 44px)", minHeight: "520px" }}
        data-testid="nest-bubble-nav"
      >
        {/* Live ticker — top-left */}
        <div className="absolute top-3 left-3 sm:top-5 sm:left-5 z-30 flex items-center gap-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] pointer-events-none">
          <span className="live-dot" aria-hidden="true" />
          <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Live · Streaming</span>
        </div>

        {/* Market mood pill — top-right */}
        {!loading && totalAssets > 0 && (
          <div className="absolute top-3 right-3 sm:top-5 sm:right-5 z-30 flex items-center gap-2 sm:gap-3 backdrop-blur-sm bg-black/60 border border-amber-500/40 rounded-full px-3 sm:px-4 py-1.5 drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
            <div className="text-right">
              <p className="text-[8px] uppercase tracking-wider text-amber-200/70">Mood</p>
              <p className={`font-heading text-xs sm:text-sm font-bold ${greenPct >= 60 ? "text-emerald-300" : greenPct <= 40 ? "text-red-300" : "text-amber-300"}`}>
                {greenPct >= 60 ? "Risk-On" : greenPct <= 40 ? "Risk-Off" : "Mixed"}
              </p>
            </div>
            <div className="h-7 w-px bg-amber-500/40" />
            <div className="text-right">
              <p className="text-[8px] uppercase tracking-wider text-amber-200/70">24h</p>
              <p className="font-data text-[11px] sm:text-xs">
                <span className="text-emerald-400">↑{gainers}</span>
                <span className="text-zinc-600 mx-1">·</span>
                <span className="text-red-400">↓{losers}</span>
              </p>
            </div>
            <button
              data-testid="refresh-prices-btn"
              onClick={() => fetchFeatured(true)}
              disabled={refreshing}
              title="Refresh prices"
              className="ml-1 text-amber-300 hover:text-amber-100 disabled:opacity-50"
            >
              <ArrowsClockwise size={13} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        )}

        {/* "Alive goose" — decorative breathing overlays. pointer-events:none so they
            never intercept bubble clicks. (Aura removed — it was glowing behind the
            title; the title now carries its own glow.) */}
        <div className="goose-glint" aria-hidden="true" />
        <div className="goose-smoke" aria-hidden="true">
          <span className="puff puff-1" />
          <span className="puff puff-2" />
          <span className="puff puff-3" />
        </div>

        {/* Center title — sits in the middle of the bubble constellation */}
        <div
          className="nest-title pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[15] text-center"
          aria-hidden="true"
        >
          {/* Ornamental flourish — top */}
          <div className="nest-flourish nest-flourish-top" aria-hidden="true">
            <span className="nest-flourish-line" />
            <span className="nest-flourish-diamond" />
            <span className="nest-flourish-line" />
          </div>

          <h1
            className="font-heading font-black uppercase nest-title-text"
            style={{ fontSize: "clamp(30px, 6vmin, 76px)", lineHeight: 0.92, letterSpacing: "0.16em" }}
            data-testid="nest-title"
          >
            <span className="block">The Golden</span>
            <span className="block">Nest</span>
          </h1>

          {/* Tagline */}
          <p
            className="nest-tagline mt-3 font-heading uppercase"
            style={{ fontSize: "clamp(8px, 1.05vmin, 11px)", letterSpacing: "0.45em" }}
          >
            Data · Education · Tools
          </p>

          {/* Ornamental flourish — bottom */}
          <div className="nest-flourish nest-flourish-bottom" aria-hidden="true">
            <span className="nest-flourish-line" />
            <span className="nest-flourish-diamond" />
            <span className="nest-flourish-line" />
          </div>
        </div>

        {/* === 3D BUBBLE NAVIGATION ===
            All tabs are visible 3D bubbles arranged in a constellation around the
            goose. Positions are responsive and the bubbles auto-scale with viewport,
            so they stay aligned + clickable on iPad, mobile, desktop. */}
        <Bubble to="/forecasting"          label="Forecasting"         top="9%"  left="50%" delay="0s"   />
        <Bubble to="/live-news"            label="Live News"           top="20%" left="17%" delay="0.3s" />
        <Bubble to="/golden-summary"       label="Golden Formula"      top="20%" left="83%" delay="0.6s" />
        <Bubble to="/tokens"               label="Crypto"              top="40%" left="9%"  delay="0.9s" />
        <Bubble to="/silly-goose"          label="Silly Goose"         top="40%" left="91%" delay="1.2s" gold />
        <Bubble to="/financial-education"  label="Financial Education" top="64%" left="11%" delay="1.5s" />
        <Bubble to="/golden-goals"         label="Golden Goals"        top="64%" left="89%" delay="1.8s" />
        <Bubble to="/economics"            label="Economic Data"       top="84%" left="22%" delay="2.1s" />
        <Bubble to="/academy"              label="Crypto Academy"      top="84%" left="78%" delay="2.4s" />
        <Bubble to="/profile"              label="Profile"             top="94%" left="50%" delay="2.7s" small />

        {/* Scroll hint */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-20 text-center drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] pointer-events-none">
          <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-0.5">Scroll for live market data</p>
          <div className="text-amber-400 animate-bounce text-sm">▾</div>
        </div>
      </section>

      {/* LIVE MARKET DATA — appears below the bubble hero, on a dark scrim so it stays readable on top of the wallpaper */}
      <div className="relative bg-zinc-950/85 backdrop-blur-sm p-5 space-y-6 max-w-[1600px] mx-auto">

      {/* Trending Alerts - Volume Spikes */}
      <section className="border-l-2 border-violet-500/50 border-t border-r border-b border-zinc-800/80 rounded-sm p-4 grad-violet">
        <TrendingAlerts />
      </section>

      {/* Market Sentiment - Fear/Greed, BTC Dominance, Alt Season */}
      <section className="border-l-2 border-blue-500/50 border-t border-r border-b border-zinc-800/80 rounded-sm p-4 grad-blue">
        <MarketSentiment />
      </section>

      {/* Favorites */}
      {favAssets.length > 0 && (
        <section className="border-l-2 border-amber-500/60 border-t border-r border-b border-zinc-800/80 rounded-sm p-4 grad-amber" data-testid="favorites-section">
          <div className="flex items-center gap-2 mb-3">
            <Star size={14} weight="fill" className="text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.6)]" />
            <h2 className="text-[10px] uppercase tracking-[0.15em] text-amber-300 font-bold">Favorites</h2>
            <span className="text-[9px] text-zinc-500">{favAssets.length}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {favAssets.map(a => <AssetCard key={`fav-${a.id}`} asset={a} onFavToggle={refreshFavs} />)}
          </div>
        </section>
      )}

      {/* Crypto — BTC, ETH, SOL, BNB */}
      <section className="border-l-2 border-amber-500/40 border-t border-r border-b border-zinc-800/80 rounded-sm p-4 bg-zinc-900/10">
        <h2 className="text-[10px] uppercase tracking-[0.15em] text-amber-400/80 mb-3 font-bold flex items-center gap-1.5">
          <CurrencyBtc size={12} weight="bold" /> Cryptocurrency
        </h2>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 bg-zinc-900/50" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="crypto-grid">
            {cryptoAssets.map(a => <AssetCard key={a.id} asset={a} onFavToggle={refreshFavs} />)}
          </div>
        )}
      </section>

      {/* Stocks */}
      <section className="border-l-2 border-emerald-500/40 border-t border-r border-b border-zinc-800/80 rounded-sm p-4 bg-zinc-900/10">
        <h2 className="text-[10px] uppercase tracking-[0.15em] text-emerald-400/80 mb-3 font-bold flex items-center gap-1.5">
          <TrendUp size={12} weight="bold" /> Stocks &amp; Macro
        </h2>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 bg-zinc-900/50" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stocks-grid">
            {otherAssets.map(a => <AssetCard key={a.id} asset={a} onFavToggle={refreshFavs} />)}
          </div>
        )}
      </section>
      </div>
    </div>
  );
}

/* ---- Unified 3D bubble used for ALL dashboard navigation tabs.
       Visible glassy sphere with specular highlight, rim light, cast shadow,
       and a gentle float animation. Scales with the viewport so the hero
       fits cleanly on iPad / mobile / desktop. ---- */
function Bubble({ to, label, top, left, delay = "0s", small = false, gold = false }) {
  const navigate = useNavigate();
  const sizeStyle = small
    ? { width: "clamp(54px, 8.5vmin, 82px)", height: "clamp(72px, 11vmin, 108px)" }
    : { width: "clamp(74px, 12vmin, 124px)", height: "clamp(98px, 16vmin, 162px)" };
  return (
    <button
      onClick={() => navigate(to)}
      data-testid={`nest-bubble-${to.replace(/^\//, "")}`}
      title={label}
      aria-label={label}
      style={{ top, left, animationDelay: delay, ...sizeStyle }}
      className={`bubble-3d ${gold ? "bubble-3d-gold" : ""} absolute -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center text-center font-heading font-bold uppercase tracking-wider text-amber-50 cursor-pointer p-2 leading-tight select-none`}
    >
      <span className="bubble-3d-label" style={{ fontSize: small ? "clamp(8px, 1.1vmin, 10px)" : "clamp(9px, 1.3vmin, 12px)" }}>
        {label}
      </span>
    </button>
  );
}
